import React, { useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';

import Menu from 'components/Menu/Menu';
import modalStyles from 'components/modalStyles';
import Personas from 'components/Personas/Personas';
import SubscriptionCancellationDate from 'components/SubscriptionCancellationDate/SubscriptionCancellationDate';
import SuccessCheckmark from 'components/SuccessCheckmark/SuccessCheckmark';
import StatusChip from 'Customers/components/StatusChip/StatusChip';
import { PACKAGE_NAMES } from 'Customers/constants';
import AddAdminModal from 'Customers/CustomerAccounts/AddAdminModal/AddAdminModal';
import {
  useMutationSuspendCustomer,
  useMutationActivateCustomer,
  useMutationArchiveCustomer,
  useMutationSyncCustomerSubscription,
  useMutationRetryFailedCustomer,
  useMutationCancelTenantSubscription,
} from 'Customers/hooks/useCustomers';
import { getAccountOptions } from 'Customers/utility';
import formatSubscriptionBusinessDate from 'utility/formatSubscriptionBusinessDate';
import './CustomerInsightRow.module.scss';

const addModalStyle = cloneDeep(modalStyles);
addModalStyle.content.width = '640px';
addModalStyle.content.padding = '0';

const CustomerInsightRow = ({
  customerName,
  fullDomain,
  subdomain,
  subscriptionStatus,
  tenantId,
  subscriptionPlan,
  accountStatus,
  renewalDate,
  cancellationDate,
  lastLogin,
  users,
  paymentConfirmation,
  renewalStatus,
  updateList,
  onSelectRow,
}) => {
  const accountType = 'LIVE_INSTANCE';
  const status = accountStatus;
  const account = { accountType, subdomain, status, id: tenantId };
  const [addAdminModal, setAddAdminModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [notification, setNotification] = useState(null);

  const { mutate: suspendCustomer } = useMutationSuspendCustomer();
  const { mutate: archiveCustomer } = useMutationArchiveCustomer();
  const { mutate: syncSubscription } = useMutationSyncCustomerSubscription();
  const { mutate: activateCustomer } = useMutationActivateCustomer();
  const { mutate: retryFailedCustomer } = useMutationRetryFailedCustomer();
  const { mutate: cancelSubscription } = useMutationCancelTenantSubscription();

  const accountOptions = getAccountOptions(status, accountType);

  const handleSuccess = (message) => {
    updateList();
    setNotification({
      show: true,
      success: true,
      message: message || 'Action completed successfully',
    });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  const handleError = (error, action) => {
    setNotification({
      show: true,
      success: false,
      message: error?.response?.data?.message || `Failed to ${action}`,
    });
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  };

  const handleAction = (action) => {
    if (loadingAction) return; // Prevent multiple actions

    switch (action) {
      case 'Suspend':
        setLoadingAction('Suspend');
        suspendCustomer(tenantId, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Customer suspended successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'suspend customer');
          },
        });
        break;
      case 'SyncSubscription':
        setLoadingAction('SyncSubscription');
        syncSubscription(tenantId, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Subscription synced successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'sync subscription');
          },
        });
        break;
      case 'AddAdmin':
        setAddAdminModal(true);
        break;
      case 'Activate':
        setLoadingAction('Activate');
        activateCustomer(tenantId, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Customer activated successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'activate customer');
          },
        });
        break;
      case 'Retry':
        setLoadingAction('Retry');
        retryFailedCustomer(tenantId, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Retry initiated successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'retry customer setup');
          },
        });
        break;
      case 'Archive':
        setLoadingAction('Archive');
        archiveCustomer(tenantId, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Customer archived successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'archive customer');
          },
        });
        break;
      case 'Delete':
        setLoadingAction('Delete');
        cancelSubscription(subdomain, {
          onSuccess: () => {
            setLoadingAction(null);
            handleSuccess('Subscription cancelled successfully');
          },
          onError: (error) => {
            setLoadingAction(null);
            handleError(error, 'cancel subscription');
          },
        });
        break;
      default:
        break;
    }
  };

  return (
    <>
      <tr onClick={() => onSelectRow(account)}>
        <td className='d-flex align-items-center py-3'>
          <div className='ms-3 d-flex gap-2a align-items-center'>
            <Personas name={customerName} />
            <p>{customerName}</p>
          </div>
        </td>
        <td>
          <span>
            {status === 'FAILED' ? (
              <i className='icon-not-verified-fill text-red me-2' />
            ) : (
              <i className='icon-verified1-fill text-green me-2' />
            )}
            {fullDomain}
          </span>
        </td>
        <td>
          <StatusChip status={status} />
        </td>
        <td> {PACKAGE_NAMES[subscriptionPlan] || subscriptionPlan || '- -'}</td>
        <td>
          <StatusChip status={subscriptionStatus} />
        </td>
        <td>
          {formatSubscriptionBusinessDate(renewalDate) || '- -'}
          <SubscriptionCancellationDate status={subscriptionStatus} cancellationDate={cancellationDate} />
        </td>
        <td>{renewalStatus === null ? 'No Action' : renewalStatus ? 'Confirm Renewal' : 'Cancel Subscription'}</td>
        <td>{paymentConfirmation ? 'Confirmed' : 'No Action'}</td>
        <td>{lastLogin ? moment(lastLogin).fromNow() : '- -'}</td>
        <td>
          {status !== 'PENDING' && accountOptions.length > 0 && (
            <div className='d-flex align-items-center'>
              <Menu data={accountOptions} onAction={(action) => handleAction(action)} isDisabled={!!loadingAction} />
              {loadingAction && (
                <span className='ms-2 text-muted small'>
                  <i className='icon-loading icon-spin me-1' />
                  {loadingAction === 'SyncSubscription'
                    ? 'Syncing...'
                    : loadingAction === 'Archive'
                    ? 'Archiving...'
                    : loadingAction === 'Suspend'
                    ? 'Suspending...'
                    : loadingAction === 'Activate'
                    ? 'Activating...'
                    : loadingAction === 'Retry'
                    ? 'Retrying...'
                    : loadingAction === 'Delete'
                    ? 'Cancelling...'
                    : 'Processing...'}
                </span>
              )}
            </div>
          )}
        </td>
      </tr>
      <AddAdminModal
        isOpen={addAdminModal}
        id={tenantId}
        users={users}
        onClose={() => {
          setAddAdminModal(false);
        }}
      />
      {notification?.show && (
        <SuccessCheckmark positionFixed text={notification.message} isFailed={!notification.success} />
      )}
    </>
  );
};

export default CustomerInsightRow;
